'use client';

import React, { useEffect, useState, useRef } from 'react';
import styles from './scss/orchestrate.module.scss';
import { assets } from '../../styles/assets/assets';
import { orchestrationAgentGoals } from '../../apiservice/aiLycaPilot';

// Typing text component
const TypingText = ({
  text,
  onDone,
  speed = 50,
}: {
  text: string;
  onDone?: () => void;
  speed?: number;
}) => {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setDisplayedText(text.slice(0, i + 1));
      i++;
      if (i === text.length) {
        clearInterval(interval);
        onDone?.();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed, onDone]);

  return <>{displayedText}</>;
};

// Sections and goals
const sections = [
  {
    title: 'Churn Probability',
    desc: 'Identify customers most likely to leave and act early with insights that drive retention.',
    caption: 'AI Insights',
    footer: 'AI Insight',
  },
];

// const grcontents = [
//   { id: 1, name: 'Increase 30-day incremental ARPU', caption: 'Approval Required', icon: <img src={assets.Chevron_icon} alt="" /> },
//   { id: 2, name: 'Approval Required', caption: 'Approval Required', icon: <img src={assets.Chevron_icon} alt="" /> },
//   { id: 3, name: 'Upsell to Smart+ 499 plan', caption: 'Approval Required', icon: <img src={assets.Chevron_icon} alt="" /> },
//   { id: 4, name: 'Upsell to Smartf+ 499 plan', caption: 'Approval Rfequired', icon: <img src={assets.Chevron_icon} alt="" /> },
//   { id: 5, name: 'Upsell to Smartf+ 499 plan', caption: 'Approval Rfequired', icon: <img src={assets.Chevron_icon} alt="" /> },
// ];

const Orchestrate1 = ({ onComplete }: { onComplete?: () => void }) => {
  const current = sections[0];

  const [phase, setPhase] = useState<'title' | 'desc' | 'goalName' | 'goalCaption' | 'goalIcon' | 'footer' | 'done'>('title');
  const [currentGoalIndex, setCurrentGoalIndex] = useState(0);

  // Refs for scrolling
  const titleRef = useRef<HTMLParagraphElement>(null);
  const descRef = useRef<HTMLSpanElement>(null);
  const captionRef = useRef<HTMLParagraphElement>(null);
  const footerRef = useRef<HTMLParagraphElement>(null);

  const [loader, setLoader] = useState(false);
  const [goal, setGoal] = useState<any>(null);
  const [agentGoals, setAgentGoals] = useState<any>(null);
  useEffect(() => {
    const fetchNlpData = async () => {
      try {
        setLoader(true);
        const res = await orchestrationAgentGoals();
        if (res?.status === 200) {
          const firstData = Object.keys(res.data.message) ?? null;
          setGoal(res.data.message)
          setAgentGoals(firstData);
        }
      } catch (error) {
        console.error("Error fetching NLP data:", error);
      } finally {
        setLoader(false);
      }
    };

    fetchNlpData();
  }, []);
  const grcontents = agentGoals?.map((res: any, key: number) => ({
    id: key + 1,
    name: res,
    caption: 'Approval Required',
    icon: (
      <img
        src={assets.Chevron_icon}
        alt=""
      />
    ),
  }));

  // Auto-scroll to current element
  useEffect(() => {
    let el: HTMLElement | null = null;
    if (phase === 'title') el = titleRef.current;
    else if (phase === 'desc') el = descRef.current;
    else if (phase === 'goalName' || phase === 'goalCaption') el = captionRef.current;
    else if (phase === 'goalIcon' || phase === 'goalIcon') el = captionRef.current;
    else if (phase === 'footer') el = footerRef.current;

    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [phase]);

  // Move to next phase sequentially
 const handleDone = () => {
  if (phase === 'title') {
    // Move from title → description
    setPhase('desc');

  } else if (phase === 'desc') {
    // Move from description → first goal name
    setPhase('goalName');

  } else if (phase === 'goalName') {
    // After goal name, show goal icon
    setPhase('goalIcon');

  } else if (phase === 'goalIcon') {
    // Check if current goal is NOT the last one
    if (currentGoalIndex < grcontents.length-1 ) {
      // Move to next goal
      setCurrentGoalIndex((prev) => prev + 1);
      setPhase('goalName');
         
    } else {
       setPhase('done');
    onComplete?.();
      // All goals shown → go to footer
      setPhase('footer');
    }

  } else if (phase === 'footer') {
    // Footer complete → mark as done
    setPhase('done');
    onComplete?.();
  }
};


  useEffect(() => {
    if (phase === 'goalIcon') {
      const timer = setTimeout(() => handleDone(), 500);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  return (
    <div className={styles.clvchartssec}>
      <div className={styles.clvchartwrapper}>
        {/* Header */}
        <div className={styles.clvwrapheader}>
          <p className={styles.clvwrapheadertxt} ref={titleRef}>
            {phase === 'title' ? <TypingText text={current.title} onDone={handleDone} /> : current.title}
          </p>
          <span className={styles.clvwrpadesc} ref={descRef}>
            {phase === 'desc' ? <TypingText text={current.desc} onDone={handleDone} /> : phase !== 'title' && current.desc}
          </span>
        </div>

        {/* Goals */}
        {['goalName', 'goalCaption', 'goalIcon', 'footer', 'done'].includes(phase) && (
          <div className={styles.clvwrapbody}>
            {grcontents.map((goal:any, index:any) => (
              <div className={styles.goalrow} key={goal.id}>
                {index === currentGoalIndex && (
                  <>
                    {/* Step 1: goal name */}
                    {(phase === 'goalName' || phase === 'goalCaption' || phase === 'goalIcon') && (
                      <div className={styles.grcontent} ref={captionRef}>
                        {phase === 'goalName' ? (
                          <TypingText text={goal.name} onDone={() => setPhase('goalCaption')} />
                        ) : (
                          goal.name
                        )}
                      </div>
                    )}

                    {/* Step 2: goal caption */}
                    {(phase === 'goalCaption' || phase === 'goalIcon') && (
                      <div className={`${styles.grstatus} ${styles.grstatustext}`} ref={captionRef}>
                        {phase === 'goalCaption' ? (
                          <TypingText text={goal.caption} onDone={() => setPhase('goalIcon')} />
                        ) : (
                          goal.caption
                        )}
                      </div>
                    )}

                    {/* Step 3: goal icon */}
                    {phase === 'goalIcon' && (
                      <div className={styles.grstatus} ref={captionRef}>
                        {goal.icon}
                      </div>
                    )}
                  </>
                )}

                {index < currentGoalIndex && (
                  <>
                    <div className={styles.grcontent}>{goal.name}</div>
                    <div className={`${styles.grstatus} ${styles.grstatustext}`}>{goal.caption}</div>
                    <div className={styles.grstatus}>{goal.icon}</div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {/* {['footer', 'done'].includes(phase) && (
          <div className={styles.clvwrapfooter}>
            <p className={styles.clvwrapfttxt} ref={footerRef}>
              {phase === 'footer' ? <TypingText text={current.footer} onDone={handleDone} /> : current.footer}
            </p>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default Orchestrate1;  